import { motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import {
    FiAlignCenter,
    FiAlignLeft,
    FiAlignRight,
    FiBold,
    FiCode,
    FiEye,
    FiImage,
    FiItalic,
    FiLink,
    FiList,
    FiMail,
    FiSave,
    FiSend,
    FiType,
    FiUnderline,
    FiZap
} from 'react-icons/fi';

const ContentCreator = () => {
  const [activeTab, setActiveTab] = useState('newsletter');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('paragraph');
  const [isPreview, setIsPreview] = useState(false);
  const textareaRef = useRef(null);

  const tabs = [
    { 
      id: 'newsletter', 
      label: 'Newsletter', 
      icon: FiMail, 
      gradient: 'from-cyan-400 via-blue-500 to-indigo-600',
      bgGradient: 'from-cyan-500/20 via-blue-500/20 to-indigo-600/20'
    },
    { 
      id: 'blog', 
      label: 'Blog Post', 
      icon: FiType, 
      gradient: 'from-violet-400 via-purple-500 to-pink-600',
      bgGradient: 'from-violet-500/20 via-purple-500/20 to-pink-600/20'
    }
  ];

  const formatOptions = [
    { value: 'paragraph', label: 'Paragraph', prefix: '', suffix: '' },
    { value: 'h1', label: 'Heading 1', prefix: '# ', suffix: '' },
    { value: 'h2', label: 'Heading 2', prefix: '## ', suffix: '' },
    { value: 'h3', label: 'Heading 3', prefix: '### ', suffix: '' },
    { value: 'quote', label: 'Quote', prefix: '> ', suffix: '' },
    { value: 'code', label: 'Code Block', prefix: '```\n', suffix: '\n```' }
  ];

  const formatButtons = [
    { icon: FiBold, label: 'Bold', shortcut: 'Ctrl+B', color: 'text-cyan-400', prefix: '**', suffix: '**' },
    { icon: FiItalic, label: 'Italic', shortcut: 'Ctrl+I', color: 'text-emerald-400', prefix: '*', suffix: '*' },
    { icon: FiUnderline, label: 'Underline', shortcut: 'Ctrl+U', color: 'text-violet-400', prefix: '<u>', suffix: '</u>' },
    { icon: FiCode, label: 'Code', shortcut: 'Ctrl+`', color: 'text-orange-400', prefix: '`', suffix: '`' },
    { icon: FiLink, label: 'Insert Link', shortcut: 'Ctrl+K', color: 'text-green-400', prefix: '[', suffix: '](url)' },
    { icon: FiList, label: 'Bullet List', shortcut: 'Ctrl+Shift+8', color: 'text-red-400', prefix: '- ', suffix: '' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        type: "spring",
        bounce: 0.3
      }
    }
  };

  const floatingAnimation = {
    y: [-8, 8, -8],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Get selected text and cursor position
  const getSelectionInfo = () => {
    const textarea = textareaRef.current;
    if (!textarea) return { start: 0, end: 0, selectedText: '' };
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    return { start, end, selectedText };
  };

  // Insert text at cursor position
  const insertTextAtCursor = (textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start, end } = getSelectionInfo();
    const newContent = content.substring(0, start) + textToInsert + content.substring(end);
    
    setContent(newContent);
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
    }, 0);
  };

  // Wrap selected text with formatting
  const wrapSelectedText = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start, end, selectedText } = getSelectionInfo();
    
    if (selectedText) {
      // Wrap selected text
      const newText = prefix + selectedText + suffix;
      const newContent = content.substring(0, start) + newText + content.substring(end);
      setContent(newContent);
      
      // Restore selection
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + newText.length);
      }, 0);
    } else {
      // No selection, just insert formatting markers
      const placeholder = prefix === '**' ? 'bold text' : 
                         prefix === '*' ? 'italic text' :
                         prefix === '`' ? 'code' :
                         prefix === '[' ? 'link text' :
                         'text';
      const newText = prefix + placeholder + suffix;
      insertTextAtCursor(newText);
    }
  };

  // Handle formatting button clicks
  const handleFormatClick = (formatAction) => {
    const button = formatButtons.find(b => b.label === formatAction);
    if (!button) return;

    switch (formatAction) {
      case 'Bold':
        wrapSelectedText('**', '**');
        break;
      case 'Italic':
        wrapSelectedText('*', '*');
        break;
      case 'Underline':
        wrapSelectedText('<u>', '</u>');
        break;
      case 'Code':
        wrapSelectedText('`', '`');
        break;
      case 'Insert Link':
        const url = prompt('Enter URL:');
        if (url) {
          const { selectedText } = getSelectionInfo();
          const linkText = selectedText || 'link text';
          wrapSelectedText(`[${linkText}](${url})`, '');
        }
        break;
      case 'Bullet List':
        const { start } = getSelectionInfo();
        const lineStart = content.lastIndexOf('\n', start - 1) + 1;
        const newContent = content.substring(0, lineStart) + '- ' + content.substring(lineStart);
        setContent(newContent);
        break;
      default:
        break;
    }
  };

  // Handle alignment (adds CSS classes in preview)
  const handleAlignment = (alignment) => {
    const { start } = getSelectionInfo();
    const lineStart = content.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = content.indexOf('\n', start);
    const actualLineEnd = lineEnd === -1 ? content.length : lineEnd;
    
    const line = content.substring(lineStart, actualLineEnd);
    const alignmentMarker = alignment === 'left' ? '' : 
                           alignment === 'center' ? '<center>' : 
                           '<right>';
    
    const newLine = alignmentMarker + line.replace(/^(<center>|<right>)/, '');
    const newContent = content.substring(0, lineStart) + newLine + content.substring(actualLineEnd);
    setContent(newContent);
  };

  // Handle format dropdown change
  const handleFormatChange = (formatValue) => {
    setSelectedFormat(formatValue);
    const format = formatOptions.find(f => f.value === formatValue);
    if (!format) return;

    const { start } = getSelectionInfo();
    const lineStart = content.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = content.indexOf('\n', start);
    const actualLineEnd = lineEnd === -1 ? content.length : lineEnd;
    
    const line = content.substring(lineStart, actualLineEnd);
    const cleanLine = line.replace(/^(#{1,3} |> |\*\*\*\n|```\n)/, '');
    
    const newLine = format.prefix + cleanLine + format.suffix;
    const newContent = content.substring(0, lineStart) + newLine + content.substring(actualLineEnd);
    setContent(newContent);
  };

  // Insert image
  const handleInsertImage = () => {
    const imageUrl = prompt('Enter image URL:');
    const altText = prompt('Enter alt text (optional):') || 'image';
    if (imageUrl) {
      insertTextAtCursor(`![${altText}](${imageUrl})`);
    }
  };

  // AI Writing Assistant (placeholder for now)
  const handleAIAssist = () => {
    const suggestions = [
      "Consider adding a compelling call-to-action at the end of your content.",
      "Your content could benefit from more specific examples or case studies.",
      "Try breaking up long paragraphs for better readability.",
      "Consider adding subheadings to organize your content better.",
      "You might want to include relevant statistics or data to support your points."
    ];
    
    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
    alert(`AI Suggestion: ${randomSuggestion}`);
  };

  // Load template
  const handleLoadTemplate = (templateName) => {
    const templates = {
      'Welcome Series': `# Welcome to Our Newsletter! 🎉

Dear Subscriber,

Thank you for joining our community! We're excited to have you on board.

## What to Expect

- **Weekly Updates**: Get the latest news and insights
- **Exclusive Content**: Access to premium articles and resources  
- **Community**: Connect with like-minded professionals

Stay tuned for amazing content!

Best regards,
The Team`,

      'Weekly Update': `# Weekly Update - ${new Date().toLocaleDateString()}

## This Week's Highlights

### 📈 Key Metrics
- New subscribers: **250+**
- Engagement rate: **8.5%**
- Top performing content: **AI in Business**

### 🔥 Trending Topics
1. Industry insights
2. Product updates
3. Community highlights

### 📅 Coming Up Next Week
- Webinar on digital transformation
- New product launch
- Q&A session with experts

Have a great week ahead!`,

      'Product Launch': `# Introducing Our Latest Innovation! 🚀

We're thrilled to announce the launch of our newest product that will revolutionize the way you work.

## Key Features
- ✨ **Advanced AI Integration**
- 🛡️ **Enterprise-Grade Security** 
- 📊 **Real-time Analytics**
- 🔗 **Seamless Integrations**

## Special Launch Offer
Get **30% off** for the first 100 customers!

[**Get Started Now**](your-link-here)

*Limited time offer - don't miss out!*`,

      'Event Announcement': `# You're Invited! 📅

## ${activeTab === 'newsletter' ? 'Exclusive Webinar' : 'Industry Conference 2024'}

**Date**: ${new Date(Date.now() + 7*24*60*60*1000).toLocaleDateString()}
**Time**: 2:00 PM - 4:00 PM EST
**Location**: Virtual / Online

### What You'll Learn
- Industry best practices
- Expert insights and trends
- Networking opportunities
- Q&A with thought leaders

### Featured Speakers
- **John Smith** - CEO, Tech Innovations
- **Sarah Johnson** - Director of Product Strategy
- **Mike Chen** - Lead Data Scientist

[**Reserve Your Spot**](registration-link)

*Seats are limited - register today!*`
    };

    const template = templates[templateName];
    if (template) {
      setContent(template);
      setTitle(templateName);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          handleFormatClick('Bold');
          break;
        case 'i':
          e.preventDefault();
          handleFormatClick('Italic');
          break;
        case 'u':
          e.preventDefault();
          handleFormatClick('Underline');
          break;
        case 'k':
          e.preventDefault();
          handleFormatClick('Insert Link');
          break;
        case '`':
          e.preventDefault();
          handleFormatClick('Code');
          break;
      }
    }
  };

  const handleSave = () => {
    const savedContent = {
      title,
      content,
      type: activeTab,
      savedAt: new Date().toISOString()
    };
    
    // Save to localStorage for demo
    localStorage.setItem('contentCreator_draft', JSON.stringify(savedContent));
    alert('Content saved successfully! 💾');
  };

  const handleSend = () => {
    if (!title.trim() || !content.trim()) {
      alert('Please add both title and content before publishing! 📝');
      return;
    }
    
    alert(`${activeTab === 'newsletter' ? 'Newsletter' : 'Blog post'} "${title}" published successfully! 🎉`);
  };

  // Enhanced preview with proper markdown rendering
  const renderPreview = () => {
    let processedContent = content;
    
    // Convert markdown-like syntax to HTML
    processedContent = processedContent
      .replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold mb-4 text-gray-800">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold mb-3 text-gray-700">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-xl font-bold mb-2 text-gray-700">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-200 px-2 py-1 rounded font-mono text-sm">$1</code>')
      .replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-4">$1</blockquote>')
      .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto my-4 rounded-lg shadow-md" />')
      .replace(/^<center>(.*$)/gm, '<div class="text-center">$1</div>')
      .replace(/^<right>(.*$)/gm, '<div class="text-right">$1</div>')
      .replace(/\n/g, '<br />');

    return { __html: processedContent };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative overflow-hidden">
      {/* Spectacular Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-violet-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-1000"></div>
      </div>

      <div className="relative z-10 p-6 space-y-8 max-w-7xl mx-auto">
        {/* Spectacular Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <motion.h1 
                  className="text-4xl font-bold bg-gradient-to-r from-pink-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent font-display mb-2"
                  animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                  transition={{ duration: 5, repeat: Infinity }}
                >
                  Content Creator Studio 
                  <motion.span 
                    className="inline-block ml-3 text-3xl"
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    ✨
                  </motion.span>
                </motion.h1>
                <p className="text-xl text-gray-300 font-body">
                  Create stunning <span className="text-pink-400 font-semibold">newsletters</span> and <span className="text-violet-400 font-semibold">blog posts</span> with advanced formatting
                </p>
              </div>
              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsPreview(!isPreview)}
                  className="bg-gradient-to-r from-slate-700/80 to-gray-700/80 backdrop-blur-xl border border-white/20 text-gray-300 px-6 py-3 rounded-xl hover:bg-gradient-to-r hover:from-slate-600/80 hover:to-gray-600/80 transition-all duration-300 flex items-center space-x-2 font-accent shadow-lg"
                >
                  <FiEye size={18} />
                  <span>{isPreview ? 'Edit' : 'Preview'}</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  className="bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2 font-accent shadow-lg"
                >
                  <FiSave size={18} />
                  <span>Save Draft</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSend}
                  className="bg-gradient-to-r from-pink-500 via-violet-500 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2 font-accent shadow-lg"
                >
                  <FiSend size={18} />
                  <span>Publish</span>
                </motion.button>
              </div>
            </div>
            
            {/* Floating elements */}
            <motion.div
              className="absolute top-6 right-32 w-3 h-3 bg-pink-400 rounded-full"
              animate={floatingAnimation}
            />
            <motion.div
              className="absolute bottom-6 left-32 w-2 h-2 bg-violet-400 rounded-full"
              animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
            />
          </div>
        </motion.div>

        {/* Enhanced Content Type Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl">
            <nav className="flex space-x-4">
              {tabs.map((tab, index) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-2xl font-accent transition-all duration-300 ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.bgGradient} border border-white/20 text-white shadow-2xl`
                      : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10'
                  }`}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    activeTab === tab.id 
                      ? `bg-gradient-to-br ${tab.gradient} shadow-lg` 
                      : 'bg-white/5'
                  }`}>
                    <tab.icon size={20} className="text-white" />
                  </div>
                  <span className="font-bold text-lg">{tab.label}</span>
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeContentTab"
                      className="w-2 h-2 bg-white rounded-full shadow-lg"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* Content Editor Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Main Editor */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-2 space-y-6"
          >
            {/* Title Input */}
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <label className="block text-lg font-bold text-gray-300 mb-4 font-body">
                {activeTab === 'newsletter' ? 'Newsletter' : 'Blog Post'} Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`Enter your ${activeTab === 'newsletter' ? 'newsletter' : 'blog post'} title...`}
                className="w-full px-6 py-4 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white placeholder-gray-400 rounded-2xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 font-heading text-xl"
              />
            </div>

            {/* Formatting Toolbar */}
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl">
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {/* Format Selector */}
                <select
                  value={selectedFormat}
                  onChange={(e) => handleFormatChange(e.target.value)}
                  className="px-4 py-3 bg-gradient-to-r from-slate-700/50 to-gray-700/50 backdrop-blur-xl border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-pink-500 transition-all duration-300 font-body"
                >
                  {formatOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="w-px h-8 bg-white/20"></div>

                {/* Format Buttons */}
                <div className="flex flex-wrap gap-2">
                  {formatButtons.map((button, index) => (
                    <motion.button
                      key={button.label}
                      onClick={() => handleFormatClick(button.label)}
                      className={`p-3 rounded-xl ${button.color} bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300 group relative`}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      title={`${button.label} (${button.shortcut})`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.05 }}
                    >
                      <button.icon size={18} />
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-black/90 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap font-mono z-50">
                        {button.label} ({button.shortcut})
                      </div>
                    </motion.button>
                  ))}
                  
                  {/* Alignment Buttons */}
                  <div className="w-px h-8 bg-white/20"></div>
                  
                  {[
                    { icon: FiAlignLeft, action: () => handleAlignment('left'), color: 'text-amber-400' },
                    { icon: FiAlignCenter, action: () => handleAlignment('center'), color: 'text-pink-400' },
                    { icon: FiAlignRight, action: () => handleAlignment('right'), color: 'text-blue-400' },
                    { icon: FiImage, action: handleInsertImage, color: 'text-purple-400' }
                  ].map((btn, index) => (
                    <motion.button
                      key={index}
                      onClick={btn.action}
                      className={`p-3 rounded-xl ${btn.color} bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300`}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <btn.icon size={18} />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Rich Text Editor */}
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Start writing your ${activeTab === 'newsletter' ? 'newsletter content' : 'blog post'}...

💡 Pro Tips:
- Use **bold** for emphasis
- Use *italic* for subtle emphasis  
- Use # for headings
- Use > for quotes
- Use - for bullet points
- Use [text](url) for links`}
                  className="w-full h-80 px-6 py-6 bg-gradient-to-br from-slate-700/30 to-gray-700/30 backdrop-blur-xl border border-white/20 text-white placeholder-gray-400 rounded-t-2xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 font-body text-lg leading-relaxed resize-none"
                  style={{ lineHeight: '1.8' }}
                />
                
                {/* Word Count */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-black/50 text-gray-300 text-sm rounded-lg font-mono">
                  {content.length} characters
                </div>
              </div>

              {/* Live Preview Section */}
              <motion.div 
                className="bg-gradient-to-br from-white/95 to-gray-50/95 backdrop-blur-xl rounded-b-2xl border-t-0 border border-white/20 shadow-inner"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200/50 bg-gradient-to-r from-gray-100/50 to-white/50">
                  <div className="flex items-center space-x-3">
                    <motion.div 
                      className="w-3 h-3 bg-green-400 rounded-full"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <h4 className="text-sm font-bold text-gray-700 font-heading">Live Preview</h4>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 font-mono">
                    <span>{content.split(' ').filter(word => word.length > 0).length} words</span>
                    <span>•</span>
                    <span>{Math.max(1, Math.ceil(content.split(' ').length / 200))} min read</span>
                  </div>
                </div>
                
                <div className="p-6 max-h-64 overflow-y-auto">
                  {content ? (
                    <>
                      {title && (
                        <motion.h1 
                          className="text-2xl font-bold mb-4 text-gray-800 font-display"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {title}
                        </motion.h1>
                      )}
                      <motion.div 
                        className="prose prose-sm max-w-none text-gray-700"
                        dangerouslySetInnerHTML={renderPreview()}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                      <div className="text-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-2"
                        />
                        <p className="text-sm font-body">Start typing to see live preview...</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Live Preview Footer */}
                <div className="px-6 py-2 bg-gradient-to-r from-blue-50/50 to-purple-50/50 border-t border-gray-200/50 rounded-b-2xl">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span className="font-mono">Updated: {new Date().toLocaleTimeString()}</span>
                      <span>•</span>
                      <span className="font-mono">Auto-refresh enabled</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <motion.div 
                        className="w-2 h-2 bg-green-400 rounded-full"
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <span className="font-mono">Live</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            variants={itemVariants}
            className="space-y-6"
          >
            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <h3 className="text-xl font-bold bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent mb-6 font-display">
                Quick Actions
              </h3>
              <div className="space-y-4">
                {[
                  { icon: FiZap, label: 'AI Writing Assistant', color: 'from-cyan-500 to-blue-600', action: handleAIAssist },
                  { icon: FiImage, label: 'Insert Stock Photo', color: 'from-emerald-500 to-green-600', action: handleInsertImage },
                  { icon: FiMail, label: 'Email Template', color: 'from-violet-500 to-purple-600', action: () => handleLoadTemplate('Welcome Series') },
                  { icon: FiCode, label: 'HTML Source', color: 'from-amber-500 to-orange-600', action: () => alert('HTML mode coming soon! 🚀') }
                ].map((action, index) => (
                  <motion.button
                    key={action.label}
                    onClick={action.action}
                    className={`w-full flex items-center space-x-3 p-4 bg-gradient-to-r ${action.color}/20 hover:${action.color}/30 border border-white/10 rounded-xl transition-all duration-300 group`}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <action.icon className="text-white" size={20} />
                    </div>
                    <span className="text-white font-medium font-body">{action.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Templates */}
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-6 font-display">
                {activeTab === 'newsletter' ? 'Newsletter' : 'Blog'} Templates
              </h3>
              <div className="space-y-3">
                {[
                  'Welcome Series',
                  'Weekly Update',
                  'Product Launch',
                  'Event Announcement'
                ].map((template, index) => (
                  <motion.button
                    key={template}
                    onClick={() => handleLoadTemplate(template)}
                    className="w-full text-left p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl transition-all duration-300 group"
                    whileHover={{ scale: 1.02, x: 5 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                  >
                    <div className="text-white font-medium font-body">{template}</div>
                    <div className="text-gray-400 text-sm font-body mt-1">Click to load template</div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <h3 className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent mb-6 font-display">
                Content Stats
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Words', value: content.split(' ').filter(word => word.length > 0).length },
                  { label: 'Reading Time', value: `${Math.max(1, Math.ceil(content.split(' ').length / 200))} min` },
                  { label: 'Characters', value: content.length },
                  { label: 'Paragraphs', value: content.split('\n\n').filter(p => p.trim().length > 0).length }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    className="flex justify-between items-center p-3 bg-white/5 rounded-xl"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                  >
                    <span className="text-gray-300 font-body">{stat.label}</span>
                    <span className="text-white font-bold font-mono">{stat.value}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Enhanced Preview Mode */}
        {isPreview && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl"
          >
            <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent mb-6 font-display">
              Preview - {activeTab === 'newsletter' ? 'Newsletter' : 'Blog Post'}
            </h3>
            <div className="bg-white rounded-2xl p-8 text-gray-900 max-h-96 overflow-y-auto">
              <h1 className="text-3xl font-bold mb-6 text-gray-800">{title || 'Untitled'}</h1>
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={renderPreview()}
              />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ContentCreator; 